// app/components/Header.jsx
import { Link, useLocation, useNavigate, useFetcher } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { MantineProvider, Menu, Button } from '@mantine/core'
import { Bars3Icon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import Modal from '../components/modal'
import DonorBox from '../components/donorBox'
import GoogleTranslate from './GoogleTranslate'
import { useTranslationMessage } from '../hooks/useTranslationMessage'

const Header = ({ title, subtitle, backgroundimage, ishomepage, shouldGetDarker, bgProp }) => {
  const showTranslationMessage = useTranslationMessage()

  const location = useLocation()
  const navigate = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false)
  // local state for Airtable stories
  const [donationStories, setDonationStories] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)

  // Remix fetcher
  const fetcher = useFetcher()

  // Listening to fetcher response; update local donationStories
  useEffect(() => {
    if (fetcher.data?.success) {
      // If we got data, store it
      setDonationStories(fetcher.data.records || [])
      setCurrentIndex(0)
    } else {
      // If it fails, set an empty array
      setDonationStories([])
    }
  }, [fetcher.data])

  const handleLoginClick = (e) => {
    e.preventDefault()
    setIsModalOpen(true)
  }

  const closeModal = () => setIsModalOpen(false)

  // On donate click, we trigger fetch if not already fetched
  const handleDonateClick = () => {
    setIsDonateModalOpen(true)
    // If we've never fetched before, call fetcher.load
    // If we already have donationStories, we just open the modal again
    if (donationStories.length === 0) {
      fetcher.load('/api/testimonials') // calls the loader in /api/testimonials
    } else {
      // If we already have stories in state, cycle or randomize them.
      // Example: cycle forward in a loop
      setCurrentIndex((prevIndex) => (prevIndex + 1) % donationStories.length)
    }
  }

  const closeDonateModal = () => {
    setIsDonateModalOpen(false)
    if (location.hash === '#donate') {
      navigate(location.pathname) // remove #donate from URL
    }
  }

  // Current story from state
  const currentStory = donationStories.length > 0 ? donationStories[currentIndex] : null

  // Open the donate modal by a #donate link
  useEffect(() => {
    if (location.hash === '#donate') {
      handleDonateClick()
    } else {
      closeDonateModal()
    }
  }, [location.hash])

  return (
    <>
      {/* Translation Warning Message */}
      {showTranslationMessage && (
        <div className="sticky top-0 z-50 bg-gradient-to-r from-gradient1 to-gradient1_100 text-white text-sm font-medium shadow-md">
          <div className="max-w-7xl mx-auto px-4 py-2 text-center">
            The translation provided is a machine translation. It may not be 100% accurate.
          </div>
        </div>
      )}

      <header
        className={`w-full relative bg-cover ${bgProp ? bgProp : 'bg-top'} ${ishomepage ? 'h-[40rem]' : 'h-[30rem]'}`}
        style={{
          backgroundImage: `${shouldGetDarker ? 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.15)),' : ''} url(${backgroundimage})`,
        }}
      >
        <div className={`bg-gray-900 bg-opacity-20 h-full ${menuOpen && !isModalOpen ? 'opacity-75' : ''}`}>
          <div className="container mx-auto">
            <div className="relative z-10 px-8 py-3 flex justify-between items-center">
              <div className="flex space-x-4">
                <Link
                  to="/"
                  reloadDocument
                  className="block w-24 h-12 bg-center bg-no-repeat bg-cover"
                  style={{
                    backgroundImage: 'url(/images/logo.png)',
                  }}
                ></Link>
              </div>

              <nav className="flex justify-between items-center">
                <MantineProvider>
                  <div className="flex items-center space-x-1">
                    <div className="hidden lg:flex items-center space-x-1">
                      <Menu trigger="click-hover" position="bottom-start">
                        <Menu.Target
                          className="text-grey30 hover:text-white text-btn-line font-btn"
                          rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                        >
                          <Button variant="subtle">EMPLOYERS</Button>
                        </Menu.Target>
                        <Menu.Dropdown className="bg-secondary border-0 !w-48">
                          <Menu.Item
                            component={Link}
                            to="/employers"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Find talent
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/talent"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Explore talent pool
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/employer-hire"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            I have a candidate I want to hire
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>

                      <Menu trigger="hover" position="bottom-start">
                        <Menu.Target
                          className="text-grey30 hover:text-white text-btn-line font-btn"
                          rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                        >
                          <Button variant="subtle">JOB SEEKERS</Button>
                        </Menu.Target>
                        <Menu.Dropdown className="bg-secondary border-0 !w-48">
                          <Menu.Item
                            component={Link}
                            to="/jobseekers"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Discover talent platform
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/candidate-hire"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            A Canadian employer wants to hire me
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/eligibility"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Eligibility
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/eligibility#faq"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Frequently asked questions (FAQs)
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>

                      <Menu trigger="click-hover" position="bottom-start">
                        <Menu.Target
                          className="text-grey30 hover:text-white text-btn-line font-btn"
                          rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                        >
                          <Button variant="subtle">WHY TALENTLIFT</Button>
                        </Menu.Target>
                        <Menu.Dropdown className="bg-secondary border-0 !w-48">
                          <Menu.Item
                            component={Link}
                            to="/about"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Our vision
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/team"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Team
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/funders-and-partners"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Partners & funders
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/news"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            News
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/resources"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Resources
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                      <Menu trigger="click-hover" position="bottom-start">
                        <Menu.Target
                          className="text-grey30 hover:text-white text-btn-line font-btn"
                          rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                        >
                          <Button variant="subtle">GIVING</Button>
                        </Menu.Target>
                        <Menu.Dropdown className="bg-secondary border-0 !w-48">
                          <Menu.Item
                            component={Link}
                            to="/donate"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Donate
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/donate"
                            reloadDocument
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Governance & finances
                          </Menu.Item>
                          <Menu.Item
                            component={Link}
                            to="/donate#faq"
                            className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                          >
                            Frequently asked questions (FAQs)
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                      <Link
                        to="#donate"
                        onClick={handleDonateClick}
                        className="py-2 px-4 text-grey30 hover:text-white focus:outline-none text-btn-line font-btn"
                      >
                        DONATE
                      </Link>
                      {/* Include the GoogleTranslate component */}
                      <GoogleTranslate />
                      <Link
                        to="#"
                        className="py-2 px-4 bg-pointed text-white text-btn-small rounded-full bg-gradient-to-r hover:from-gradient2 hover:to-gradient2_80 font-btn"
                        onClick={handleLoginClick}
                      >
                        LOGIN
                      </Link>
                    </div>
                  </div>
                </MantineProvider>
              </nav>
            </div>
            <div className="relative z-10 p-6 lg:px-24 lg:py-10 text-white mt-[8rem] max-w-[60rem] mx-auto lg:mx-0">
              <h1 className="text-display-sm lg:text-display md:text-display-md mb-4">{title}</h1>
              <p className="text-body mb-5">{subtitle}</p>
              {ishomepage && (
                <>
                  <Link
                    to="/employers"
                    reloadDocument
                    className="group mr-4 bg-gradient-to-r from-gradient1 to-gradient1_100 hover:bg-blue-700 text-white text-btn-icon px-[1.3rem] py-[0.8rem] rounded-full inline-flex items-center mb-4"
                  >
                    <ChevronUpIcon className="rotate-45 w-6 mr-2 pb-[6px] transform transition-transform duration-300 group-hover:rotate-90 group-hover:pb-[-6px]" />
                    FOR EMPLOYERS
                  </Link>

                  <Link
                    to="/jobseekers"
                    reloadDocument
                    className="group bg-gradient-to-r from-gradient2 to-gradient2_80 hover:bg-purple-700 text-white text-btn-icon px-[1.3rem] py-[0.8rem] rounded-full inline-flex items-center"
                  >
                    <ChevronUpIcon className="rotate-45 w-6 mr-2 pb-[6px] transform transition-transform duration-300 group-hover:rotate-90" />
                    FOR CANDIDATES
                  </Link>
                </>
              )}
            </div>
            {isModalOpen && (
              <Modal isOpen={isModalOpen} closeModal={closeModal}>
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/2">
                    <img src="/images/login.png" alt="Family Image" className="w-full h-auto" />
                  </div>
                  <div className="md:w-1/2 flex flex-col p-8 container mx-auto">
                    <h2 className="text-headline text-center">Login to TalentLift</h2>
                    <div className="mt-6">
                      <a href="https://platform.talentlift.ca" target="_blank">
                        <button className="group w-full text-left flex items-center justify-between px-6 py-4 border rounded-[1rem] shadow-sm mb-4 hover:bg-gray-50">
                          Job seeker
                          <ChevronUpIcon className="rotate-45 w-6 mr-2 pb-[6px] transform transition-transform duration-300 group-hover:text-pointed group-hover:rotate-90 group-hover:pb-[-6px]" />
                        </button>
                      </a>
                      <a href="https://employer.talentlift.ca" target="_blank">
                        <button className="group w-full text-left flex items-center justify-between px-6 py-4 border rounded-[1rem] shadow-sm hover:bg-gray-50">
                          Employer
                          <ChevronUpIcon className="rotate-45 w-6 mr-2 pb-[6px] transform transition-transform duration-300 group-hover:text-pointed group-hover:rotate-90 group-hover:pb-[-6px]" />
                        </button>
                      </a>
                    </div>
                  </div>
                </div>
              </Modal>
            )}

            {isDonateModalOpen && (
              <Modal isOpen={isDonateModalOpen} closeModal={closeDonateModal} modalWidth="max-w-[74rem]">
                <div className="flex flex-col lg:flex-row w-full">
                  {/* Image Section (70% width) */}

                  <div className="relative w-full lg:w-[70%]">
                    {/* Background Image */}
                    <div
                      className="hidden lg:block absolute inset-0 bg-cover bg-right"
                      style={{
                        backgroundImage: `url(${currentStory && currentStory.photo ? currentStory.photo : '/images/stories/tahsin.jpg'})`,
                      }}
                    ></div>

                    {/* Dark Overlay to make the image darker */}
                    <div className="hidden lg:block absolute inset-0 bg-secondary opacity-50 z-10"></div>

                    {/* Gradient Overlay between image and DonorBox */}
                    <div className="hidden lg:block absolute inset-y-0 right-0 w-1/4 bg-gradient-to-r from-transparent to-secondary z-10"></div>

                    {/* Gradient Overlay between image and DonorBox */}
                    <div className="lg:hidden absolute inset-x-0 bottom-0 h-full bg-gradient-to-t from-secondary to-secondary z-10"></div>

                    {/* Content (text over image) */}
                    {/* Content (text over image) */}
                    <div className="relative z-30 p-4 lg:p-14 text-white h-full flex flex-col justify-between">
                      {/* Title at the top */}
                      <h2 className="text-display-sm lg:text-display-md">Donate to TalentLift</h2>

                      {/* Text and Author at the bottom */}
                      {currentStory && (
                        <div className="hidden lg:block">
                          <p className="mt-4 text-body">"{currentStory.text}"</p>
                          <p className="mt-4 text-body">- {currentStory.author}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DonorBox Section (30% width) */}
                  <div className="w-full lg:w-[30%] bg-secondary p-4 lg:p-8 flex items-center justify-center">
                    <div className="text-white text-2xl  max-w-[350px]">
                      <DonorBox />
                    </div>
                  </div>
                </div>
              </Modal>
            )}
          </div>
        </div>

        {menuOpen && (
          <div className="absolute inset-0 bg-gray-900 bg-opacity-95 flex flex-col items-center justify-start pt-[4.5rem] z-10">
            <MantineProvider>
              <Menu trigger="click">
                <Menu.Target
                  className="text-white text-xl font-normal mb-4"
                  rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                >
                  <Button variant="subtle">EMPLOYERS</Button>
                </Menu.Target>
                <Menu.Dropdown className="bg-secondary border-0 !w-48">
                  <Menu.Item
                    component={Link}
                    to="/employers"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Find talent
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/talent"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Explore talent pool
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/employer-hire"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    I have a candidate I want to hire
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <Menu trigger="click">
                <Menu.Target
                  className="text-white text-xl font-normal mb-4"
                  rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                >
                  <Button variant="subtle">JOB SEEKERS</Button>
                </Menu.Target>
                <Menu.Dropdown className="bg-secondary border-0 !w-48">
                  <Menu.Item
                    component={Link}
                    to="/jobseekers"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Discover talent platform
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/candidate-hire"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    A Canadian employer wants to hire me
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/eligibility"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Eligibility
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/eligibility#faq"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Frequently asked questions (FAQs)
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <Menu trigger="click">
                <Menu.Target
                  className="text-white text-xl font-normal mb-4"
                  rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                >
                  <Button variant="subtle">WHY TALENTLIFT</Button>
                </Menu.Target>
                <Menu.Dropdown className="bg-secondary border-0 !w-48">
                  <Menu.Item
                    component={Link}
                    to="/about"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Our vision
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/team"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Team
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/funders-and-partners"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Partners & funders
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/news"
                    reloadDocument
                    className="text-white hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    News
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/resources"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Resources
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Menu trigger="click">
                <Menu.Target
                  className="text-white text-xl font-normal mb-4"
                  rightSection={<ChevronDownIcon style={{ width: '1rem', height: '1rem' }} />}
                >
                  <Button variant="subtle">GIVING</Button>
                </Menu.Target>
                <Menu.Dropdown className="bg-secondary border-0 !w-48">
                  <Menu.Item
                    component={Link}
                    to="/donate"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Donate
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/donate"
                    reloadDocument
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Governance & finances
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to="/donate#faq"
                    className="text-white bg-secondary hover:text-primary hover:bg-secondary text-btn-line"
                  >
                    Frequently asked questions (FAQs)
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </MantineProvider>
            <Link
              to="#donate"
              className="py-2 text-white focus:outline-none text-xl font-normal mb-4"
              onClick={handleDonateClick}
            >
              DONATE
            </Link>
            {/* Include the GoogleTranslate component */}
            <div className="mb-4">
              <GoogleTranslate />
            </div>
            <Link
              to="#"
              className="py-2 px-4 bg-pointed text-white text-btn-small rounded-full bg-gradient-to-r hover:from-gradient2 hover:to-gradient2_80 font-btn"
              onClick={handleLoginClick}
            >
              LOGIN
            </Link>
          </div>
        )}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="absolute top-4 right-8 lg:hidden focus:outline-none z-20"
        >
          {menuOpen ? <XMarkIcon className="w-6 h-6 text-white" /> : <Bars3Icon className="w-6 h-6 text-white" />}
        </button>
      </header>
    </>
  )
}

export default Header
